// Types only: Chart.js is in the lazily loaded chart chunk
import type { Chart } from "chart.js";
import { ChartTooltipData } from "stores/genericStore/state";
import { Coords } from "types/app";
import {
  interpolateChartPoint,
  interpolateCoords,
  TrackPosition,
} from "utils/chart/trackPosition";
import { isValidCoords } from "utils/validators";
import type {
  ChartDataPoint,
  XAxisTooltipFormatter,
  YAxisTooltipFormatter,
} from "./TrackChart";

export type ChartHoverFormatters = {
  xAxisTooltipFormatter: XAxisTooltipFormatter;
  yAxisTooltipFormatter: YAxisTooltipFormatter;
};

/**
 * What the chart's tooltip and vertical bar and the marker on the map show at a place of the track: the values in
 * between the track's points, where the chart's line is and where the track is on the map. Null while the chart
 * and the track have different points (e.g. the route is being recomputed).
 */
export const getChartHoverData = ({
  chart,
  points,
  trackpoints,
  place,
  formatters: { xAxisTooltipFormatter, yAxisTooltipFormatter },
  isHovered,
}: {
  chart: Chart<"line", ChartDataPoint[], unknown>;
  points: ChartDataPoint[];
  trackpoints: Coords[];
  place: TrackPosition;
  formatters: ChartHoverFormatters;
  isHovered: boolean;
}): ChartTooltipData => {
  if (points.length === 0 || points.length !== trackpoints.length) return null;

  const position = interpolateCoords(trackpoints, place);
  if (!isValidCoords(position)) return null;

  const point = interpolateChartPoint(points, place);
  // The grade is the one of the hovered segment; at a point the one of the segment that leads to it
  const prevPoint =
    place.fraction > 0 ? points[place.index] : points[place.index - 1];
  const { x: xScale, y: yScale } = chart.scales;
  const { top, bottom } = chart.chartArea;

  return {
    position,
    xAxisString: xAxisTooltipFormatter(point.x),
    yAxisString: yAxisTooltipFormatter({
      prevPoint,
      currentPoint: { x: point.x, y: point.y ?? null },
    }),
    left: xScale.getPixelForValue(point.x),
    // In a gap of the data there is no line, the tooltip is in the middle
    top:
      point.y === null || point.y === undefined
        ? (top + bottom) / 2
        : yScale.getPixelForValue(point.y),
    isHovered,
  };
};
